/**
 * @file     ReltaChatBar.tsx
 * @purpose  The conversational input: a single expressive bar with suggestion
 *           chips in the idle hero state, reused as the composer inside the
 *           chat workspace.
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { useSpeechToText } from '@/features/ai-chat/useSpeechToText';

export function ReltaChatBar({
  onSend,
  disabled = false,
  showSuggestions = false,
  autoFocus = false,
  placeholder,
  ariaLabel,
  suggestions: suggestionOverrides,
  transcribeUrl,
}: {
  /** Resolves false when the message was NOT accepted (e.g. the sign-up gate
   *  opened); the text stays in the composer. */
  onSend: (text: string) => Promise<boolean> | boolean | void;
  disabled?: boolean;
  showSuggestions?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  suggestions?: string[];
  /** Server transcription endpoint used as the mic fallback where Web Speech
   *  is unavailable: record with MediaRecorder, POST the clip, append text. */
  transcribeUrl?: string;
}) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  // Coarse pointers get Enter-as-newline; sending is the button's job there.
  const [coarsePointer, setCoarsePointer] = useState(false);
  useEffect(() => {
    setCoarsePointer(window.matchMedia('(pointer: coarse)').matches);
  }, []);

  // The input is disabled while Relta answers, which drops focus; take it back
  // the moment the reply lands so the buyer can keep typing.
  const wasDisabledRef = useRef(false);
  useEffect(() => {
    if (wasDisabledRef.current && !disabled && !window.matchMedia('(pointer: coarse)').matches) {
      inputRef.current?.focus();
    }
    wasDisabledRef.current = disabled;
  }, [disabled]);

  const MAX_CHARS = 1000;

  // Tap-to-dictate (Web Speech API). Text captured since the mic started is
  // appended to whatever was already typed; each interim result replaces the
  // live tail so the input tracks speech in real time.
  const dictationBaseRef = useRef('');
  const { supported: micSupported, listening, start: startMic, stop: stopMic } = useSpeechToText((text) => {
    const base = dictationBaseRef.current;
    setValue(`${base}${base ? ' ' : ''}${text}`.slice(0, MAX_CHARS));
  });
  // Fallback for browsers without Web Speech (e.g. Firefox): record with
  // MediaRecorder and send the clip to the server transcription route.
  const [recorderSupported, setRecorderSupported] = useState(false);
  useEffect(() => {
    setRecorderSupported(
      typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia),
    );
  }, []);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  const transcribeClip = async (blob: Blob) => {
    if (!transcribeUrl || blob.size === 0) return;
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append('audio', blob, 'dictation.webm');
      const res = await secureFetch(transcribeUrl, { method: 'POST', body: form });
      if (!res.ok) return;
      const data = (await res.json()) as { text?: string };
      if (data.text) {
        const base = dictationBaseRef.current;
        setValue(`${base}${base ? ' ' : ''}${data.text}`.slice(0, MAX_CHARS));
      }
    } catch {
      /* dictation is best-effort; typed input keeps working */
    } finally {
      setTranscribing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        setRecording(false);
        void transcribeClip(new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' }));
      };
      dictationBaseRef.current = value;
      recorder.start();
      setRecording(true);
    } catch {
      /* mic permission denied; nothing to record */
    }
  };
  const stopRecording = () => {
    recorderRef.current?.stop();
  };

  const usesRecorderFallback = !micSupported && Boolean(transcribeUrl) && recorderSupported;
  const micVisible = micSupported || usesRecorderFallback;
  const micActive = listening || recording;
  const toggleMic = () => {
    if (usesRecorderFallback) {
      if (recording) { stopRecording(); return; }
      void startRecording();
      return;
    }
    if (listening) { stopMic(); return; }
    dictationBaseRef.current = value;
    startMic();
  };

  const suggestions = suggestionOverrides ?? [
    t('aiChat.suggestion1', 'Townhouse in Burnaby under $1.3M'),
    t('aiChat.suggestion2', 'Walkable condo near SkyTrain'),
    t('aiChat.suggestion3', 'Family home near good schools'),
    t('aiChat.suggestion4', 'A place with a mortgage helper'),
  ];

  const submit = async (text: string) => {
    if (listening) stopMic();
    if (recording) stopRecording();
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    const accepted = await onSend(trimmed);
    if (accepted !== false) setValue('');
    inputRef.current?.focus();
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          minWidth: 0,
          px: 2.5,
          py: 1,
          borderRadius: 1,
          backgroundColor: theme.semanticColors.surfaceCard,
          border: `1px solid ${theme.semanticColors.borderSubtle}`,
          opacity: disabled ? 0.6 : 1,
          '@container profile-panel (max-width: 600px)': {
            alignItems: 'flex-end',
            gap: 0.5,
            px: 0.75,
            py: 1,
          },
        }}
      >
        <InputBase
          fullWidth
          autoFocus={autoFocus}
          multiline
          maxRows={4}
          value={value}
          disabled={disabled}
          inputRef={inputRef}
          onChange={(e) => { if (listening) stopMic(); setValue(e.target.value.slice(0, MAX_CHARS)); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !coarsePointer) {
              e.preventDefault();
              void submit(value);
            }
          }}
          placeholder={placeholder ?? t('aiChat.placeholder', 'Tell Relta what kind of home you want')}
          inputProps={{ 'aria-label': ariaLabel ?? t('aiChat.inputLabel', 'Ask Relta about homes') }}
          sx={{
            minWidth: 0,
            fontSize: 16,
            py: 1,
            px: 1,
            color: theme.semanticColors.textPrimary,
            '@container profile-panel (max-width: 600px)': { px: 0.5 },
          }}
        />
        {micVisible && (
          <IconButton
            aria-label={micActive ? t('aiChat.micStop', 'Stop dictation') : t('aiChat.micStart', 'Dictate your message')}
            aria-pressed={micActive}
            disabled={disabled || transcribing}
            onClick={toggleMic}
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              color: micActive ? theme.semanticColors.textOnPrimary : theme.semanticColors.textSecondary,
              backgroundColor: micActive ? theme.semanticColors.statusError : 'transparent',
              transition: 'background-color 160ms ease, color 160ms ease',
              '&:hover': { backgroundColor: micActive ? theme.semanticColors.statusError : theme.semanticColors.borderSubtle },
              ...(micActive && {
                animation: 'rlta-mic-pulse 1.4s ease-in-out infinite',
                '@keyframes rlta-mic-pulse': {
                  '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(theme.rawSemanticColors.statusError, 0.4)}` },
                  '50%': { boxShadow: `0 0 0 6px ${alpha(theme.rawSemanticColors.statusError, 0)}` },
                },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }),
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="9" y="2" width="6" height="12" rx="3" />
              <path d="M5 10a7 7 0 0 0 14 0" />
              <path d="M12 17v4" />
            </svg>
          </IconButton>
        )}
        <IconButton
          aria-label={t('aiChat.send', 'Send')}
          disabled={disabled || !value.trim()}
          onClick={() => void submit(value)}
          sx={{
            backgroundColor: theme.palette.brand.primary,
            color: theme.semanticColors.textOnPrimary,
            '&:hover': { backgroundColor: theme.palette.brand.primary },
            '&.Mui-disabled': { backgroundColor: theme.semanticColors.borderSubtle },
            width: 40,
            height: 40,
            flexShrink: 0,
          }}
        >
          {/* Inline SVG instead of pulling in @mui/icons-material. */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 19V5" />
            <path d="M5 12l7-7 7 7" />
          </svg>
        </IconButton>
      </Box>

      {value.length >= MAX_CHARS - 100 && (
        <Box
          component="span"
          sx={{ display: 'block', textAlign: 'right', mt: 0.5, fontSize: 11.5, color: theme.semanticColors.textMuted }}
        >
          {value.length}/{MAX_CHARS}
        </Box>
      )}

      {showSuggestions && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 2 }}>
          {suggestions.map((s) => (
            <Chip
              key={s}
              label={s}
              onClick={() => void submit(s)}
              disabled={disabled}
              sx={{
                borderRadius: 1,
                backgroundColor: 'transparent',
                border: `1px solid ${theme.semanticColors.borderSubtle}`,
                color: theme.semanticColors.textSecondary,
                fontSize: 13,
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
